///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

/** A single point on a storm's observed or forecast track */
export interface StormTrackPoint {
	/** Valid time, epoch milliseconds UTC */
	t: number
	lat: number
	lon: number
	/** Sustained wind in knots */
	wind_kt: number | null
	/** Wind gusts in knots (forecast points only) */
	gust_kt?: number | null
	/** Minimum central pressure in millibars */
	mslp_mb: number | null
	/** Saffir-Simpson category 0-5 */
	category: number
	/** Development label at this point (e.g. "Tropical Storm") */
	type: string | null
	/** Forecast hour (forecast points only) */
	tau?: number
}

/** Wind extent from the storm center, nautical miles per compass quadrant */
export interface WindRadiiQuadrants {
	ne: number
	se: number
	sw: number
	nw: number
}

/** Current wind extent by threshold (34/50/64 kt) */
export type WindRadii = Partial<Record<"34" | "50" | "64", WindRadiiQuadrants>>

/**
 * Coastal watch/warning polylines, grouped by TCWW code:
 * TWA = Tropical Storm Watch, TWR = Tropical Storm Warning,
 * HWA = Hurricane Watch, HWR = Hurricane Warning
 */
export interface WatchWarningSegment {
	code: string
	/** One or more polylines of [lon, lat] positions along the coast */
	paths: [number, number][][]
}

/** One ECMWF track point: [tau_h, lat, lon, wind_kt | null, mslp_hpa | null] */
export type EnsembleTrackPoint = [number, number, number, number | null, number | null]

/** ECMWF open-data tropical cyclone tracks matched to a storm */
export interface EnsembleTracks {
	/** Model run, ISO 8601 UTC */
	run: string
	/** ENS members (control + perturbed), each a track from the run's analysis */
	members: Array<{ m: number, pts: EnsembleTrackPoint[] }>
	/** Deterministic HRES track, null when ECMWF published none for this system */
	hres: EnsembleTrackPoint[] | null
}

/**
 * SHIPS environmental diagnostics (NHC/ATCF stext), the forecaster's
 * checklist along the forecast hours; arrays align with `taus`, null where
 * the file said N/A. Mirrors weather-services HurricaneEntry.ShipsDiagnostics
 */
export interface ShipsDiagnostics {
	run: string
	taus: number[]
	vmaxKt: Array<number | null>
	/** 850–200 hPa shear, kt, and the heading the shear vector points toward, degrees */
	shearKt: Array<number | null>
	shearDirDeg: Array<number | null>
	sstC: Array<number | null>
	potentialIntensityKt: Array<number | null>
	/** 700–500 hPa relative humidity, % */
	rhMid: Array<number | null>
	/** Ocean heat content, kJ/cm² */
	oceanHeatContent: Array<number | null>
	landKm: Array<number | null>
	ri: Array<{ thresholdKt: number; hours: number; consensusPct: number; riiPct: number; climatologyPct: number | null }>
}

/**
 * One ATCF best-track fix: the structure parameters the advisory feed
 * lacks (km; null where the deck wrote 0 / blank). Mirrors
 * weather-services HurricaneEntry.BestTrackFix
 */
export interface BestTrackFix {
	t: string
	lat: number
	lon: number
	vmaxKt: number | null
	mslpMb: number | null
	type: string | null
	/** Pressure and radius of the outer closed isobar */
	pociMb: number | null
	rociKm: number | null
	rmwKm: number | null
	gustKt: number | null
	eyeKm: number | null
	depth: "S" | "M" | "D" | "X" | null
}

export interface BestTrack {
	source: "NHC"
	/** Oldest first */
	fixes: BestTrackFix[]
}

/**
 * NHC 120 h wind speed probability contours near the storm: nested
 * exterior rings per threshold (34 / 50 / 64 kt) and probability class
 * (5, 10, 20 … 100 %). Mirrors weather-services HurricaneEntry.WindProbability
 */
export interface WindProbabilityPolygon {
	kt: number
	pct: number
	/** Closed [lon, lat] ring */
	ring: number[][]
}

export interface WindProbability {
	/** Product issue time, ISO */
	issued: string
	polygons: WindProbabilityPolygon[]
}

export interface StormProperties extends DatasetFeature {
	id: string
	name: string
	basin: string
	storm_num: number
	classification: string
	advisory_num: string | null
	advisory_date: string
	lat: number
	lon: number
	max_wind_kt: number | null
	gust_kt: number | null
	mslp_mb: number | null
	category: number
	movement_dir: number | null
	movement_speed_kt: number | null
	/** JSON array of StormTrackPoint */
	observed_track: string
	/** JSON array of StormTrackPoint */
	forecast_track: string
	/** GeoJSON Polygon or MultiPolygon as a JSON string, null when unavailable */
	cone: string | null
	/** JSON array of WatchWarningSegment, null when no watches/warnings */
	watches_warnings: string | null
	/** JSON WindRadii object, null for storms without an NHC TCM product */
	wind_radii: string | null
	/** Advisory headline from the NHC public advisory, null for JTWC storms */
	headline: string | null
	/** JSON EnsembleTracks (ECMWF ENS + HRES), null when ECMWF tracks no matching system */
	ensemble_tracks: string | null
	/** JSON ShipsDiagnostics, null for JTWC basins */
	ships: string | null
	/** JSON BestTrack (ATCF b-deck fixes), null for JTWC basins */
	best_track: string | null
	/** JSON WindProbability (NHC 120 h wind speed probability contours), null for JTWC basins */
	wind_probability: string | null
	bbox_min_lon: number
	bbox_min_lat: number
	bbox_max_lon: number
	bbox_max_lat: number
	update_key: string
	ingested_at: string
}

const cBatchSize = 100

export class HurricanesTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists hurricanes (
					id text primary key,
					name text not null,
					basin text not null,
					storm_num integer not null,
					classification text not null,
					advisory_num text,
					advisory_date text not null,
					lat real not null,
					lon real not null,
					max_wind_kt real,
					gust_kt real,
					mslp_mb real,
					category integer not null,
					movement_dir real,
					movement_speed_kt real,
					observed_track text not null,
					forecast_track text not null,
					cone text,
					watches_warnings text,
					wind_radii text,
					headline text,
					ensemble_tracks text,
					ships text,
					best_track text,
					wind_probability text,
					bbox_min_lon real not null,
					bbox_min_lat real not null,
					bbox_max_lon real not null,
					bbox_max_lat real not null,
					update_key text not null,
					ingested_at text not null
				)`
			},
			{ sql: "create index if not exists idx_hurricanes_advisory_date on hurricanes(advisory_date desc)" },
			{ sql: "create index if not exists idx_hurricanes_category on hurricanes(category desc)" },
		])

		// Migrate existing installs: create table if not exists won't alter
		// an existing table, so add the advisory-detail columns when missing
		const columns = await db.exec("pragma table_info(hurricanes)", [], "low") as Array<{ name: string }>
		if (Array.isArray(columns) && columns.length > 0) {
			for (const column of ["watches_warnings", "wind_radii", "headline", "ensemble_tracks", "ships", "best_track", "wind_probability"]) {
				if (!columns.some(c => c.name === column)) {
					await db.exec(`alter table hurricanes add column ${column} text`)
				}
			}
		}
	}

	public static async replaceAll(rows: StormProperties[]): Promise<void> {
		await Database.db.exec("delete from hurricanes", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: StormProperties[]): Promise<void> {
		const statement = `insert into hurricanes (
				id, name, basin, storm_num, classification,
				advisory_num, advisory_date,
				lat, lon, max_wind_kt, gust_kt, mslp_mb, category,
				movement_dir, movement_speed_kt,
				observed_track, forecast_track, cone,
				watches_warnings, wind_radii, headline, ensemble_tracks, ships, best_track, wind_probability,
				bbox_min_lon, bbox_min_lat, bbox_max_lon, bbox_max_lat,
				update_key, ingested_at
			) values (
				?, ?, ?, ?, ?,
				?, ?,
				?, ?, ?, ?, ?, ?,
				?, ?,
				?, ?, ?,
				?, ?, ?, ?, ?, ?, ?,
				?, ?, ?, ?,
				?, ?
			)
			on conflict(id) do update set
				name = excluded.name,
				basin = excluded.basin,
				storm_num = excluded.storm_num,
				classification = excluded.classification,
				advisory_num = excluded.advisory_num,
				advisory_date = excluded.advisory_date,
				lat = excluded.lat,
				lon = excluded.lon,
				max_wind_kt = excluded.max_wind_kt,
				gust_kt = excluded.gust_kt,
				mslp_mb = excluded.mslp_mb,
				category = excluded.category,
				movement_dir = excluded.movement_dir,
				movement_speed_kt = excluded.movement_speed_kt,
				observed_track = excluded.observed_track,
				forecast_track = excluded.forecast_track,
				cone = excluded.cone,
				watches_warnings = excluded.watches_warnings,
				wind_radii = excluded.wind_radii,
				headline = excluded.headline,
				ensemble_tracks = excluded.ensemble_tracks,
				ships = excluded.ships,
				best_track = excluded.best_track,
				wind_probability = excluded.wind_probability,
				bbox_min_lon = excluded.bbox_min_lon,
				bbox_min_lat = excluded.bbox_min_lat,
				bbox_max_lon = excluded.bbox_max_lon,
				bbox_max_lat = excluded.bbox_max_lat,
				update_key = excluded.update_key,
				ingested_at = excluded.ingested_at`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(s => [
				s.id,
				s.name,
				s.basin,
				s.storm_num,
				s.classification,
				s.advisory_num,
				s.advisory_date,
				s.lat,
				s.lon,
				s.max_wind_kt,
				s.gust_kt,
				s.mslp_mb,
				s.category,
				s.movement_dir,
				s.movement_speed_kt,
				s.observed_track,
				s.forecast_track,
				s.cone,
				s.watches_warnings,
				s.wind_radii,
				s.headline,
				s.ensemble_tracks,
				s.ships,
				s.best_track,
				s.wind_probability,
				s.bbox_min_lon,
				s.bbox_min_lat,
				s.bbox_max_lon,
				s.bbox_max_lat,
				s.update_key,
				s.ingested_at,
			]))
		}
	}

	public static async deleteByIds(ids: string[]): Promise<void> {
		if (ids.length === 0) return
		const placeholders = ids.map(() => "?").join(",")
		await Database.db.exec(`delete from hurricanes where id in (${placeholders})`, ids, "critical")
	}

	public static async getAll(): Promise<StormProperties[]> {
		const rows = await Database.db.exec(
			`select * from hurricanes order by category desc, advisory_date desc`,
			[],
			"low"
		)
		return rows as StormProperties[]
	}

	public static async getById(id: string): Promise<StormProperties | null> {
		const rows = await Database.db.exec(
			`select * from hurricanes where id = ?`,
			[id],
			"high"
		) as StormProperties[]
		return rows[0] ?? null
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from hurricanes`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}
}

/** Parse a storm's track JSON, tolerating malformed rows */
export function parseTrack(json: string | null): StormTrackPoint[] {
	if (!json) return []
	try {
		const parsed: unknown = JSON.parse(json)
		return Array.isArray(parsed) ? parsed as StormTrackPoint[] : []
	} catch {
		return []
	}
}

/** Parse a storm's wind radii JSON, tolerating malformed rows */
export function parseWindRadii(json: string | null): WindRadii | null {
	if (!json) return null
	try {
		const parsed: unknown = JSON.parse(json)
		return parsed && typeof parsed === "object" ? parsed : null
	} catch {
		return null
	}
}

/** Parse a storm's ECMWF ensemble tracks JSON, tolerating malformed rows */
export function parseEnsembleTracks(json: string | null): EnsembleTracks | null {
	if (!json) return null
	try {
		const parsed = JSON.parse(json) as Partial<EnsembleTracks> | null
		if (!parsed || typeof parsed.run !== "string" || !Array.isArray(parsed.members)) return null
		return {
			run: parsed.run,
			members: parsed.members.filter(m => Array.isArray(m?.pts)),
			hres: Array.isArray(parsed.hres) ? parsed.hres : null,
		}
	} catch {
		return null
	}
}

/** Parse a storm's SHIPS diagnostics JSON, tolerating malformed rows */
export function parseShips(json: string | null | undefined): ShipsDiagnostics | null {
	if (!json) return null
	try {
		const parsed = JSON.parse(json) as Partial<ShipsDiagnostics> | null
		if (!parsed || typeof parsed.run !== "string" || !Array.isArray(parsed.taus)) return null
		return parsed as ShipsDiagnostics
	} catch {
		return null
	}
}

export function parseBestTrack(json: string | null | undefined): BestTrack | null {
	if (!json) return null
	try {
		const parsed = JSON.parse(json) as Partial<BestTrack> | null
		if (!parsed || !Array.isArray(parsed.fixes) || parsed.fixes.length === 0) return null
		return parsed as BestTrack
	} catch {
		return null
	}
}

export function parseWindProbability(json: string | null | undefined): WindProbability | null {
	if (!json) return null
	try {
		const parsed = JSON.parse(json) as Partial<WindProbability> | null
		if (!parsed || typeof parsed.issued !== "string" || !Array.isArray(parsed.polygons) || parsed.polygons.length === 0) return null
		return parsed as WindProbability
	} catch {
		return null
	}
}

/** Parse a storm's watches/warnings JSON, tolerating malformed rows */
export function parseWatchWarnings(json: string | null): WatchWarningSegment[] {
	if (!json) return []
	try {
		const parsed: unknown = JSON.parse(json)
		return Array.isArray(parsed) ? parsed as WatchWarningSegment[] : []
	} catch {
		return []
	}
}

/** Basin code to the agency responsible for advisories */
export function stormSource(basin: string): string {
	switch (basin.toUpperCase()) {
		case "AL":
		case "EP":
		case "CP": return "NOAA NHC"
		default: return "JTWC"
	}
}

/** Short display label like "Cat 4" / "TS" / "TD" */
export function stormCategoryLabel(storm: Pick<StormProperties, "category" | "max_wind_kt">): string {
	if (storm.category >= 1) return `Cat ${storm.category}`
	if ((storm.max_wind_kt ?? 0) >= 34) return "TS"
	return "TD"
}
